import React from 'react';
import { Card } from './ui/card';

const MyJourney = () => {
  const stories = [
    {
      company: 'Castler',
      logo: 'https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/if3jboia_Castler.png',
      role: 'VP & Head of Marketing',
      period: 'Sep 2024 - Present',
      location: 'New Delhi',
      industry: 'FinTech',
      story: 'Building marketing from the ground up for Indias largest escrow platform. In fintech, especially in regulated escrow banking, trust is everything. I am designing our entire marketing engine—brand positioning, demand generation, content strategy, and customer acquisition—for a B2B audience that includes banks, fintechs, and enterprises.',
      what: [
        'Leading all aspects of marketing: branding, digital marketing, content strategy, PR, and demand generation',
        'Building thought leadership in the regulated FinTech space',
        'Creating marketing systems designed for B2B enterprise sales cycles',
      ],
      impact: 'Currently building the foundation for scale—positioning Castler as the trusted name in escrow banking.',
    },
    {
      company: 'Satvic Movement',
      logo: 'https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/fmzl4qi3_SM.png',
      role: 'Head of Marketing',
      period: 'Jan 2024 - Aug 2024',
      location: 'Gurgaon',
      industry: 'Non-Profit / Health & Wellness',
      story: 'This was unique. Satvic Movement had 10 million followers and 1 billion YouTube views, but needed to scale workshops and community engagement systematically. Working directly with the founders, I led a complete rebranding and GTM launch—then built a performance marketing engine that balanced growth with authenticity.',
      what: [
        'Led rebranding and go-to-market launch for D2C health platform',
        'Managed ₹10Cr performance marketing budget focused on workshop registrations',
        'Built a high-performing marketing team from scratch',
        'Drove outreach and community growth while maintaining brand authenticity',
      ],
      impact: 'Scaled workshop registrations by 5x. Maintained 90%+ engagement rate while growing to new markets.',
    },
    {
      company: 'IndiaMART',
      logo: 'https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/gjd9756s_IndiaMART.png',
      role: 'AVP & Head - Marketing & Product',
      period: 'Jun 2021 - Nov 2023',
      location: 'Noida',
      industry: 'B2B Marketplace',
      story: 'IndiaMART taught me that B2B marketing is a completely different game. I led marketing and product for the Enterprise Solutions vertical—selling SaaS subscriptions to large corporates. Long sales cycles. Committee buying. CXO engagement. This was about building trust and demonstrating value at every touchpoint.',
      what: [
        'Led product marketing for Enterprise SaaS vertical targeting large corporates',
        'Drove SaaS subscription growth and client engagement strategies',
        'Built customer success stories that resonated with B2B decision-makers',
        'Developed enterprise marketing playbook for long sales cycles',
      ],
      impact: 'Achieved ₹40Cr topline for Enterprise Solutions. Enhanced client retention by 40% through strategic engagement. Positioned IndiaMART as an enterprise SaaS player, not just a marketplace.',
    },
    {
      company: "Aakash Education (BYJU's)",
      logo: 'https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/7fhlxvn6_Aakash-removebg-preview.png',
      role: 'AGM & Head - Digital Marketing',
      period: 'Jan 2018 - Jun 2021',
      location: 'New Delhi',
      industry: 'EdTech',
      story: 'Aakash was where I learned to operate at scale. I led marketing across digital and offline channels for NEET and IIT-JEE test prep—managing ₹35-40Cr annual budgets. This was not just about running campaigns. It was about building an integrated marketing engine that combined ATL/BTL, performance digital, SEO, and e-commerce into one cohesive system.',
      what: [
        'Led marketing across digital and offline channels for test prep solutions',
        'Managed ₹35-40Cr annual budgets across performance marketing, SEO, and ATL/BTL',
        'Built e-commerce strategies for education products',
        'Created data-driven attribution models to optimize spend efficiency',
      ],
      impact: 'Drove ₹110Cr+ in direct business impact. Improved customer acquisition efficiency by 3x. Successfully launched hybrid learning model reaching tier 2/3 cities.',
    },
    {
      company: 'HT Media (Hindustan Times & Mint)',
      logo: 'https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/cpe8ovpw_HT.png',
      role: 'Manager - Digital Marketing',
      period: 'Sep 2015 - Jan 2018',
      location: 'New Delhi',
      industry: 'News & Media',
      story: 'At HT Media, I managed user acquisition and ad revenue optimization across hindustantimes.com, livemint.com, livehindustan.com, and desimartini.com. This is where I learned that audience attention is the real currency—and that content, distribution, and monetization are all interconnected.',
      what: [
        'Managed user acquisition across 4 major news platforms',
        'Optimized ad revenue and impressions through audience segmentation',
        'Built data analytics framework for editorial and business decisions',
        'Created insights engine that guided content strategy',
      ],
      impact: 'Increased monthly active users by 2.5x across portfolio. Improved ad revenue per user by 60% through better targeting.',
    },
    {
      company: 'Publicis Groupe',
      logo: 'https://customer-assets.emergentagent.com/job_web-profile-19/artifacts/rojiyfjr_PG.png',
      role: 'Manager - Media Strategy',
      period: 'Apr 2013 - Sep 2015',
      location: 'Gurgaon',
      industry: 'Advertising & Media',
      story: 'This is where it all started. At Publicis Groupe (Starcom), I worked on media planning and strategy for clients like Dabur, managing ~₹1000Cr in media spends across TV, print, and radio. I learned how to optimize massive budgets, build media mix models, and work with some of Indias biggest brands. It taught me discipline, rigor, and the art of getting the most out of every rupee spent.',
      what: [
        'Managed media planning & strategy for ~₹1000Cr media spends',
        'Optimized TV campaigns for Dabur brands (Hajmola, Chyawanprash, Real Juice)',
        'Built media mix models for optimal channel allocation',
        'Worked with major FMCG and consumer brands',
      ],
      impact: 'Improved media efficiency by 25% through data-driven optimization. Reduced wastage in TV planning while maintaining reach.',
    },
  ];

  return (
    <section id="journey" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">The Journey</h2>
          <p className="text-lg text-slate-600 max-w-3xl">
            Twelve years. Six industries. Multiple countries. Here is the story of what I built, 
            what I learned, and how each experience shaped how I think about marketing.
          </p>
        </div>

        <div className="space-y-12">
          {stories.map((story, index) => (
            <Card key={index} className="overflow-hidden border-slate-200 hover:shadow-xl transition-shadow duration-300">
              <div className="p-8">
                <div className="mb-6">
                  <div className="flex items-start justify-between gap-6 mb-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">{story.company}</h3>
                      <div className="text-lg font-semibold text-teal-600 mb-1">{story.role}</div>
                      <div className="text-sm text-slate-600">{story.period} • {story.location}</div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-32 h-20 flex items-center justify-center bg-white rounded-lg border border-slate-200 p-3">
                        <img
                          src={story.logo}
                          alt={`${story.company} logo`}
                          className="max-w-full max-h-full object-contain filter grayscale"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <span className="inline-block text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">{story.industry}</span>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-slate-700 leading-relaxed text-lg">{story.story}</p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">What I Did</h4>
                    <ul className="space-y-2">
                      {story.what.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-700">
                          <span className="text-teal-600 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Impact</h4>
                    <p className="text-slate-800 font-medium">{story.impact}</p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MyJourney;